import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiError, fmtDate } from "@/lib/api";
import { PageHead } from "@/pages/AppLayout";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Gift, UserPlus, Trash2, Plus, Power, Lock, Users, Truck, Ticket, Building2, Ban, UserX, Send, LifeBuoy, Recycle, Check, ChevronRight } from "lucide-react";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card p-5 border-t-4 border-[#FFB800]" data-testid={`admin-stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{label}</p>
        <Icon className="w-4 h-4 text-[#94A3B8]" />
      </div>
      <p className="font-mono text-4xl mt-2 text-[#FFB800]">{value}</p>
    </div>
  );
}

export default function Admin() {
  const nav = useNavigate();
  const [ov, setOv] = useState(null);
  const [comps, setComps] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [fleets, setFleets] = useState([]);
  const [banned, setBanned] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [dc, setDc] = useState({ code: "", percent_off: "", max_redemptions: "" });
  const [bc, setBc] = useState({ title: "", body: "", all: true, ids: [] });
  const [faults, setFaults] = useState([]);
  const [cleaning, setCleaning] = useState(false);
  const [crmStats, setCrmStats] = useState(null);

  const load = async () => {
    const [o, c, d, u, m, b, f, fl, cs] = await Promise.all([
      api.get("/admin/overview"), api.get("/admin/comp-emails"), api.get("/admin/discounts"),
      api.get("/admin/users"), api.get("/admin/machines"), api.get("/admin/banned"),
      api.get("/admin/fault-reports"), api.get("/admin/fleets"), api.get("/admin/crm/stats"),
    ]);
    setOv(o.data); setComps(c.data); setDiscounts(d.data); setUsers(u.data); setMachines(m.data); setBanned(b.data); setFaults(f.data); setFleets(fl.data); setCrmStats(cs.data);
  };

  const cleanupOrphans = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/cleanup-orphan-fleets");
      toast.success(data.orphan_fleets_removed ? `Removed ${data.orphan_fleets_removed} orphan fleet(s)` : "No orphan fleets found");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setCleaning(false); }
  };

  const resolveFault = async (id, status) => {
    try { await api.patch(`/admin/fault-reports/${id}`, { status }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const doRemove = async () => {
    const ban = confirm.mode === "ban";
    try { await api.delete(`/admin/users/${confirm.id}?ban=${ban}`); toast.success(ban ? "User removed & banned" : "User removed"); setConfirm(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); setConfirm(null); }
  };
  const unban = async (email) => {
    try { await api.delete(`/admin/banned/${encodeURIComponent(email)}`); toast.success("Email unbanned"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const sendBroadcast = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/broadcast", { title: bc.title, body: bc.body, user_ids: bc.all ? null : bc.ids });
      toast.success(`Sent to ${data.sent} ${data.sent === 1 ? "user" : "users"}`);
      setBc({ title: "", body: "", all: true, ids: [] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);

  const addComp = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/comp-emails", { email: newEmail }); setNewEmail(""); toast.success("Complimentary access granted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const removeComp = async (email) => {
    try { await api.delete(`/admin/comp-emails/${encodeURIComponent(email)}`); toast.success("Access revoked"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const createDiscount = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/discounts", {
        code: dc.code, percent_off: parseInt(dc.percent_off),
        max_redemptions: dc.max_redemptions ? parseInt(dc.max_redemptions) : null,
      });
      setDc({ code: "", percent_off: "", max_redemptions: "" }); toast.success("Discount code created"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const toggleDiscount = async (code) => { await api.patch(`/admin/discounts/${code}`); load(); };
  const deleteDiscount = async (code) => { await api.delete(`/admin/discounts/${code}`); toast.success("Deleted"); load(); };

  return (
    <div className="p-5 md:p-10 pb-24" data-testid="admin-page">
      <PageHead title="Owner Admin" sub="Manage free access and discount codes across all fleets." />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <Stat icon={Building2} label="Fleets" value={ov?.fleets ?? "—"} />
        <Stat icon={Users} label="Users" value={ov?.users ?? "—"} />
        <Stat icon={Truck} label="Machines" value={ov?.machines ?? "—"} />
        <Stat icon={Gift} label="Comped" value={ov?.comp_emails ?? "—"} />
        <Stat icon={Ticket} label="Discounts" value={ov?.discounts ?? "—"} />
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <button onClick={cleanupOrphans} disabled={cleaning} className="btn-ghost h-11 px-4 font-head text-sm flex items-center gap-2" data-testid="cleanup-orphans-btn">
          <Recycle className="w-4 h-4" /> {cleaning ? "Cleaning…" : "Clean Up Orphan Fleets"}
        </button>
      </div>

      {/* CRM */}
      <button onClick={() => nav("/admin/crm")} className="card w-full text-left p-5 mb-4 flex items-center gap-4 group hover:border-[#FFB800]/50 transition-colors" data-testid="admin-crm-link">
        <div className="w-11 h-11 bg-[#0D0E11] border border-white/10 flex items-center justify-center shrink-0"><Users className="w-5 h-5 text-[#FFB800]" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-head font-700 text-xl uppercase tracking-wide group-hover:text-[#FFB800] transition-colors flex items-center gap-2">CRM — Leads
            {crmStats?.new_this_week > 0 && <span className="badge bg-[#00E676]/15 text-[#00E676] border-[#00E676]/40" data-testid="crm-new-week-badge">{crmStats.new_this_week} new this week</span>}
          </p>
          <p className="font-body text-sm text-[#94A3B8]">Track potential customers, pipeline and activity.</p>
        </div>
        <ChevronRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#FFB800]" />
      </button>

      {/* All fleets */}
      <div className="card mb-4" data-testid="admin-fleets">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><Building2 className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-xl uppercase tracking-wide">Fleets ({fleets.length})</h2></div>
        <div className="divide-y divide-white/10">
          {fleets.map((f) => (
            <button key={f.id} onClick={() => nav(`/admin/fleets/${f.id}`)} className="w-full text-left flex items-center gap-4 px-5 py-4 group hover:bg-[#1A1C23] transition-colors cursor-pointer" data-testid={`admin-fleet-${f.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-head font-700 text-lg truncate flex items-center gap-1.5 group-hover:text-[#FFB800] transition-colors">{f.name}<ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#FFB800]" /></p>
                <p className="font-mono text-xs text-[#94A3B8] truncate">{f.owner_name} · {f.owner_email}</p>
              </div>
              <span className={`badge ${f.comp ? "badge-upcoming" : "badge-healthy"} shrink-0`}>{f.plan}</span>
              <span className="font-mono text-xs text-[#94A3B8] shrink-0 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {f.members}</span>
              <span className="font-mono text-xs text-[#94A3B8] shrink-0 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {f.machines}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="card mb-4" data-testid="admin-faults">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10">
          <LifeBuoy className="w-5 h-5 text-[#FFB800]" />
          <h2 className="font-head font-800 uppercase tracking-tight text-xl">Fault Reports</h2>
          <span className="font-mono text-xs text-[#94A3B8]">({faults.filter((f) => f.status !== "resolved").length} open)</span>
        </div>
        {faults.length === 0 ? (
          <p className="px-5 py-4 font-body text-sm text-[#94A3B8]">No fault reports.</p>
        ) : (
          <div className="divide-y divide-white/10">
            {faults.map((f) => (
              <div key={f.id} className="px-5 py-4 flex items-start gap-3" data-testid={`fault-${f.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-head font-700">{f.subject}</p>
                    <span className={`badge ${f.status === "resolved" ? "badge-healthy" : "badge-overdue"}`}>{f.status}</span>
                  </div>
                  <p className="font-body text-sm text-white/90 mt-1">{f.message}</p>
                  {f.has_screenshot && <img src={`${API}/admin/fault-reports/${f.id}/photo?token=${localStorage.getItem("ironlog_token")}`} alt="Screenshot" className="mt-2 max-h-40 border border-white/10" data-testid={`fault-screenshot-${f.id}`} />}
                  <p className="font-mono text-[11px] text-[#94A3B8] mt-1">{f.user_name} · {f.user_email} · {f.fleet_name || "—"} · {fmtDate(f.created_at)}</p>
                </div>
                {f.status !== "resolved"
                  ? <button onClick={() => resolveFault(f.id, "resolved")} className="btn-ghost h-9 px-3 font-head text-xs flex items-center gap-1.5 shrink-0" data-testid={`fault-resolve-${f.id}`}><Check className="w-3.5 h-3.5" /> Resolve</button>
                  : <button onClick={() => resolveFault(f.id, "open")} className="text-[#94A3B8] hover:text-white font-mono text-xs shrink-0" data-testid={`fault-reopen-${f.id}`}>Reopen</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={sendBroadcast} className="card p-6 mb-4" data-testid="broadcast-card">
        <div className="flex items-center gap-2 mb-4"><Send className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-xl uppercase tracking-wide">Send In-App Notification</h2></div>
        <div className="grid md:grid-cols-[1fr_auto] gap-3 items-center">
          <input className="il-input" placeholder="Title" value={bc.title} onChange={(e) => setBc({ ...bc, title: e.target.value })} required data-testid="broadcast-title" />
          <label className="flex items-center gap-2 font-body text-sm text-[#94A3B8] whitespace-nowrap"><input type="checkbox" checked={bc.all} onChange={(e) => setBc({ ...bc, all: e.target.checked })} data-testid="broadcast-all" /> Send to all users</label>
        </div>
        <textarea className="il-input py-3 mt-3" style={{ height: "auto", minHeight: "5rem" }} placeholder="Message" value={bc.body} onChange={(e) => setBc({ ...bc, body: e.target.value })} required data-testid="broadcast-body" />
        {!bc.all && (
          <select multiple className="il-input mt-3" style={{ height: "auto", minHeight: "7rem" }} value={bc.ids} onChange={(e) => setBc({ ...bc, ids: Array.from(e.target.selectedOptions, (o) => o.value) })} data-testid="broadcast-select">
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.email}</option>)}
          </select>
        )}
        <button className="btn-signal h-12 px-6 font-head mt-4 flex items-center gap-2" data-testid="broadcast-send"><Send className="w-4 h-4" /> Send Notification</button>
      </form>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Comp emails */}
        <div className="card">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><Gift className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-xl uppercase tracking-wide">Complimentary Access</h2></div>
          <p className="px-5 pt-3 font-body text-xs text-[#94A3B8]">Grants unlimited machines &amp; staff at no cost. These accounts stay manager-level — they do <span className="text-white">not</span> get owner/admin access.</p>
          <form onSubmit={addComp} className="flex gap-2 p-4 border-b border-white/10" data-testid="comp-add-form">
            <input className="il-input" type="email" placeholder="email@company.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required data-testid="comp-email-input" />
            <button className="btn-signal h-13 px-4 font-head flex items-center gap-1 shrink-0" style={{ height: "3.25rem" }} data-testid="comp-add-btn"><UserPlus className="w-4 h-4" /> Grant</button>
          </form>
          <div className="divide-y divide-white/10 max-h-[360px] overflow-y-auto">
            {comps.length === 0 && <p className="px-5 py-4 font-body text-sm text-[#94A3B8]">No complimentary accounts yet.</p>}
            {comps.map((c) => (
              <div key={c.email} className="flex items-center gap-3 px-5 py-3" data-testid={`comp-row-${c.email}`}>
                <span className="flex-1 font-mono text-sm truncate">{c.email}</span>
                {c.locked ? <span className="badge badge-upcoming flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>
                  : <button onClick={() => removeComp(c.email)} className="text-[#94A3B8] hover:text-[#FF3B30]" data-testid={`comp-remove-${c.email}`}><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
        </div>

        {/* Discounts */}
        <div className="card">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><Ticket className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-xl uppercase tracking-wide">Discount Codes</h2></div>
          <form onSubmit={createDiscount} className="grid grid-cols-[1fr_90px_90px_auto] gap-2 p-4 border-b border-white/10 items-end" data-testid="discount-form">
            <div><label className="il-label">Code</label><input className="il-input font-mono uppercase" placeholder="HARVEST20" value={dc.code} onChange={(e) => setDc({ ...dc, code: e.target.value.toUpperCase() })} required data-testid="discount-code-input" /></div>
            <div><label className="il-label">% off</label><input className="il-input font-mono" type="number" min="1" max="100" placeholder="20" value={dc.percent_off} onChange={(e) => setDc({ ...dc, percent_off: e.target.value })} required data-testid="discount-percent-input" /></div>
            <div><label className="il-label">Max uses</label><input className="il-input font-mono" type="number" placeholder="∞" value={dc.max_redemptions} onChange={(e) => setDc({ ...dc, max_redemptions: e.target.value })} data-testid="discount-max-input" /></div>
            <button className="btn-signal font-head flex items-center gap-1" style={{ height: "3.25rem" }} data-testid="discount-create-btn"><Plus className="w-4 h-4" /></button>
          </form>
          <div className="divide-y divide-white/10 max-h-[320px] overflow-y-auto">
            {discounts.length === 0 && <p className="p-5 font-body text-sm text-[#94A3B8]">No discount codes yet.</p>}
            {discounts.map((d) => (
              <div key={d.code} className="flex items-center gap-3 px-5 py-3" data-testid={`discount-row-${d.code}`}>
                <span className="font-mono text-lg text-[#FFB800]">{d.code}</span>
                <span className="font-mono text-sm text-white">{d.percent_off}% off</span>
                <span className="font-mono text-xs text-[#94A3B8] flex-1">{d.redemptions}{d.max_redemptions ? `/${d.max_redemptions}` : ""} used</span>
                <span className={`badge ${d.active ? "badge-healthy" : "badge-overdue"}`}>{d.active ? "Active" : "Off"}</span>
                <button onClick={() => toggleDiscount(d.code)} className="text-[#94A3B8] hover:text-white" data-testid={`discount-toggle-${d.code}`}><Power className="w-4 h-4" /></button>
                <button onClick={() => deleteDiscount(d.code)} className="text-[#94A3B8] hover:text-[#FF3B30]" data-testid={`discount-delete-${d.code}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* All users */}
      <div className="card mt-4" data-testid="admin-users">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><Users className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-xl uppercase tracking-wide">Users ({users.length})</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse">
            <thead><tr className="bg-[#0D0E11] text-[#94A3B8]">
              {["Name", "Email", "Role", "Fleet", "Joined", ""].map((h) => <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest p-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-white/10" data-testid={`admin-user-${u.id}`}>
                  <td className="p-3"><button onClick={() => nav(`/admin/users/${u.id}`)} className="font-body font-700 text-white hover:text-[#FFB800] transition-colors flex items-center gap-1.5" data-testid={`admin-user-open-${u.id}`}>{u.name} <ChevronRight className="w-3.5 h-3.5 text-[#94A3B8]" /></button></td>
                  <td className="p-3 font-mono text-sm text-[#94A3B8]">{u.email}</td>
                  <td className="p-3"><span className={`badge ${u.role === "manager" ? "badge-upcoming" : "badge-healthy"}`}>{u.role}</span></td>
                  <td className="p-3 font-body text-sm">{u.fleet}</td>
                  <td className="p-3 font-mono text-xs text-[#94A3B8]">{fmtDate(u.created_at)}</td>
                  <td className="p-3">
                    {u.is_admin ? <span className="badge badge-upcoming flex items-center gap-1"><Lock className="w-3 h-3" /> Owner</span> : (
                      <div className="flex gap-3">
                        <button onClick={() => setConfirm({ ...u, mode: "remove" })} className="text-[#94A3B8] hover:text-white" title="Remove" data-testid={`user-remove-${u.id}`}><UserX className="w-4 h-4" /></button>
                        <button onClick={() => setConfirm({ ...u, mode: "ban" })} className="text-[#94A3B8] hover:text-[#FF3B30]" title="Remove & Ban" data-testid={`user-ban-${u.id}`}><Ban className="w-4 h-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Banned emails */}
      {banned.length > 0 && (
        <div className="card mt-4" data-testid="admin-banned">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><Ban className="w-5 h-5 text-[#FF3B30]" /><h2 className="font-head font-700 text-xl uppercase tracking-wide">Banned Emails ({banned.length})</h2></div>
          <div className="divide-y divide-white/10">
            {banned.map((b) => (
              <div key={b.email} className="flex items-center gap-3 px-5 py-3" data-testid={`banned-${b.email}`}>
                <span className="flex-1 font-mono text-sm truncate">{b.email}</span>
                <span className="font-mono text-xs text-[#94A3B8]">{fmtDate(b.banned_at)}</span>
                <button onClick={() => unban(b.email)} className="btn-ghost h-9 px-3 font-head text-xs" data-testid={`unban-${b.email}`}>Unban</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All machines */}
      <div className="card mt-4" data-testid="admin-machines">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><Truck className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-xl uppercase tracking-wide">Machines ({machines.length})</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead><tr className="bg-[#0D0E11] text-[#94A3B8]">
              {["Make / Model", "Type", "Rego", "Hours", "Fleet", "Added by", "Uploaded"].map((h) => <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest p-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {machines.map((m) => (
                <tr key={m.id} onClick={() => nav(`/admin/machines/${m.id}`)} className="border-t border-white/10 hover:bg-[#1A1C23] transition-colors cursor-pointer" data-testid={`admin-machine-${m.id}`}>
                  <td className="p-3 font-head font-700 uppercase text-white hover:text-[#FFB800] transition-colors">{m.make} {m.model}</td>
                  <td className="p-3 font-mono text-xs uppercase text-[#FFB800]">{m.type}</td>
                  <td className="p-3 font-mono text-sm">{m.rego || "—"}</td>
                  <td className="p-3 font-mono text-sm">{m.hours || 0}h</td>
                  <td className="p-3 font-body text-sm">{m.fleet}</td>
                  <td className="p-3 font-body text-sm text-[#94A3B8]">{m.added_by}</td>
                  <td className="p-3 font-mono text-xs text-[#94A3B8]">{fmtDate(m.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#1A1C23] border border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head uppercase">{confirm?.mode === "ban" ? "Remove & Ban" : "Remove"} {confirm?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#94A3B8]">
              {confirm?.mode === "ban"
                ? "This permanently deletes the account and blocks this email from ever signing up again."
                : "This permanently deletes the account. They can sign up again later with the same email."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-ghost border-white/20">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRemove} className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80" data-testid="confirm-user-action">{confirm?.mode === "ban" ? "Remove & Ban" : "Remove"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
